import { useMemo, useCallback } from 'react';
import { useKeybindingContext } from './keybinding/KeybindingContext.jsx';
import { createCrossPlatformBinding } from './keybinding/utils.js';

/**
 * Hook to get commands for the command menu from the keybinding system
 */
export const useCommandMenu = () => {
  const keybindingContext = useKeybindingContext();
  
  // Stabilize the functions to prevent unnecessary re-renders
  const getAllBindings = useCallback(() => {
    return keybindingContext.getAllBindings({ active: true });
  }, [keybindingContext.getAllBindings]);
  
  const executeAction = useCallback((actionName, sequence, binding) => {
    return keybindingContext.executeAction(actionName, sequence, binding);
  }, [keybindingContext.executeAction]);
  
  const getAllActions = useCallback(() => {
    return keybindingContext.executor.getAllActions();
  }, [keybindingContext.executor]);

  const commands = useMemo(() => {
    // Get ALL bindings, not just active ones
    const bindings = keybindingContext.getAllBindings();
    const allActions = getAllActions();
    
    // Create a map of action to metadata for category lookup
    const actionMetadata = {};
    allActions.forEach(action => {
      actionMetadata[action.name] = action.metadata;
    });

    // Get cross-platform command palette key
    const commandPaletteKey = createCrossPlatformBinding('k', { primary: true });

    // Define custom category mapping for better organization
    const categoryMapping = {
      // Navigation commands
      'g c': 'Navigation',
      'g s': 'Navigation', 
      'g u': 'Navigation',
      'j': 'Navigation',
      'k': 'Navigation',
      'g g': 'Navigation',
      'Shift+G': 'Navigation',
      'd': 'Navigation',
      'u': 'Navigation',
      'y y': 'Navigation',
      'y m': 'Navigation',
      
      // Chat Controls
      's m': 'Chat Controls',
      's r': 'Chat Controls', 
      's a': 'Chat Controls',
      't f': 'Chat Controls',
      'Shift+D': 'Chat Controls',
      'n': 'Chat Controls',
      'i': 'Chat Controls',
      
      // Interface
      't m': 'Interface',
      '/': 'Interface',
      [commandPaletteKey]: 'Interface',
      'Escape': 'Interface',
    };

    // Group bindings by our custom categories
    const grouped = {};
    const commandList = [];
    
    bindings.forEach((binding) => {
      const actionMeta = actionMetadata[binding.action] || {};
      const category = categoryMapping[binding.sequence] || 'Interface';
      
      if (!grouped[category]) {
        grouped[category] = [];
      }

      // Check if command is available/enabled
      const isEnabled = binding.when ? binding.when() : true;
      const disabledReason = !isEnabled ? (binding.disabledReason || 'Not available') : null;

      const command = {
        id: binding.sequence,
        sequence: binding.sequence,
        description: binding.description || actionMeta.description || `Execute ${binding.sequence}`,
        category: category,
        action: binding.action,
        enabled: isEnabled,
        disabledReason: disabledReason,
        handler: () => {
          if (isEnabled) {
            executeAction(binding.action, binding.sequence, binding);
          }
        },
      };

      grouped[category].push(command);
      commandList.push(command);
    });

    return {
      byCategory: grouped,
      list: commandList,
    };
  }, [keybindingContext, getAllActions, executeAction]);

  return commands;
};

/**
 * Format key sequence for display
 * Returns array of key sequences, where each sequence is either a string (single key)
 * or an array of key objects (modifier combination like [{type: 'modifier', key: 'Meta'}, {type: 'key', key: 'k'}])
 */
export const formatKeySequence = (sequence) => {
  return sequence.split(' ').map(key => {
    // Handle special keys and modifiers
    if (key.includes('+')) {
      const parts = key.split('+');
      return parts.map(part => {
        switch (part) {
          case 'Meta':
            return { type: 'modifier', key: 'Meta', display: 'command' };
          case 'Control':
            return { type: 'modifier', key: 'Control', display: 'Ctrl' };
          case 'Shift':
            return { type: 'modifier', key: 'Shift', display: 'shift' };
          case 'Alt':
            return { type: 'modifier', key: 'Alt', display: 'option' };
          default:
            return { type: 'key', key: part, display: part };
        }
      });
    }
    
    // Handle special single keys
    switch (key) {
      case 'Escape':
        return { type: 'key', key: 'Escape', display: 'Esc' };
      case 'Enter':
        return { type: 'key', key: 'Enter', display: '↵' };
      case 'Space':
        return { type: 'key', key: 'Space', display: 'Space' };
      case 'ArrowUp':
        return { type: 'key', key: 'ArrowUp', display: '↑' };
      case 'ArrowDown':
        return { type: 'key', key: 'ArrowDown', display: '↓' };
      case 'ArrowLeft':
        return { type: 'key', key: 'ArrowLeft', display: '←' };
      case 'ArrowRight':
        return { type: 'key', key: 'ArrowRight', display: '→' };
      default:
        return { type: 'key', key: key, display: key };
    }
  });
};

/**
 * Get icon for command category (no icons for cleaner look)
 */
export const getCategoryIcon = (category) => {
  // Return empty string to remove icons
  return '';
};